import { AbstractExitReason, Reason } from "@kvix/shared";
import { Button, Text } from "@kvix/ui";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  makeStyles,
  MenuItem,
  Select,
  TextareaAutosize,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { format } from "date-fns";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAsyncFn } from "react-use";
import StripeInternal from "stripe";
import { PaymentContext } from "../context";

const useStyles = makeStyles((theme) => ({
  dialogReasonTextArea: {
    resize: "none",
    fontFamily: "inherit",
    padding: "12px",
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "0.5px",
    background: "#FEE9EE",
    border: 0,
    borderRadius: "8px",
    outline: "none",
  },
  dialogContainer: {
    textAlign: "center",
    padding: "38px",
    margin: "auto",
    width: "100%",
  },
}));

interface Props {
  subscription: StripeInternal.Subscription;
  open: boolean;
  onClose: () => void;
}

export const CancelSubscriptionModal: React.FC<Props> = (props) => {
  const { actions } = useContext(PaymentContext);
  const classes = useStyles(props);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [cancelReason, setCancelReason] = useState<Reason>(Reason.None);
  const [otherCancelReason, setOtherCancelReason] = useState("");

  const [{ loading }, cancelSubscription] = useAsyncFn(
    async (exitReason: Partial<AbstractExitReason>) => {
      await actions.cancelSubscription(props.subscription, exitReason);
      props.onClose();
    }
  );

  const handleCancelSubscription = async () => {
    const exitReason: Partial<AbstractExitReason> = {
      reason: cancelReason,
      reasonText: otherCancelReason,
    };

    return await cancelSubscription(exitReason);
  };

  const billingDate = new Date(props.subscription.current_period_end * 1000);
  const billing = format(billingDate, "dd/MM/yyy");
  const { t, ready } = useTranslation(["account", "general"]);
  const templateRemovalReasons: {
    id: string;
    label: string;
  }[] = t("account.deleteAccountDialog.deleteCancelReasons", {
    returnObjects: true,
  });

  const handleChangeRemovalReason = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value;
    const cancelReason: Reason =
      Reason[value.charAt(0).toUpperCase() + value.slice(1)];
    setCancelReason(cancelReason);
    setOtherCancelReason("");
  };

  if (!ready) {
    return null;
  }

  return (
    <Dialog open={props.open} maxWidth="xs" fullScreen={isMobile}>
      <div className={classes.dialogContainer}>
        <DialogContent>
          <Box mb={2}>
            <Text variant="h5">
              {t("account.planDetails.editSubscriptionModal.title")}
            </Text>
          </Box>

          <DialogContentText>
            {t("account.planDetails.editSubscriptionModal.text", { billing })}
          </DialogContentText>
          <FormControl style={{ width: "100%" }}>
            <Select
              fullWidth
              onChange={handleChangeRemovalReason}
              value={cancelReason}
              variant="outlined"
              style={{ textAlign: "start", marginBottom: "16px" }}
              required
            >
              {templateRemovalReasons.map((reason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.label}
                </MenuItem>
              ))}
            </Select>
            {cancelReason !== "none" && (
              <TextareaAutosize
                className={classes.dialogReasonTextArea}
                placeholder={
                  cancelReason === "other"
                    ? t("account.deleteAccountDialog.enterOtherReasonText")
                    : t("account.deleteAccountDialog.enterReasonText")
                }
                rowsMin={3}
                rowsMax={5}
                color="primary"
                onChange={(event) => setOtherCancelReason(event.target.value)}
              />
            )}
            {otherCancelReason.length > 200 && (
              <Alert severity="error" style={{ marginBottom: "16px" }}>
                {t("account.deleteAccountDialog.reasonTextError")}
              </Alert>
            )}
          </FormControl>
        </DialogContent>

        <DialogActions
          style={{
            flexFlow: "column",
            justifyContent: "center",
          }}
        >
          <Button
            color="primary"
            onClick={handleCancelSubscription}
            loading={loading}
            disabled={cancelReason === "none" || otherCancelReason.length > 200}
            style={{ marginBottom: "6px" }}
          >
            {t("general:buttons.cancelSubscription")}
          </Button>
          <Button color="primary" variant="text" onClick={props.onClose}>
            {t("general:buttons.goBack")}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};
